"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useWatch } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOnboardingWizard } from "../../hooks/use-onboarding-wizard";
import { OnboardingPageSkeleton } from "../page-skeleton";
import { StepAboutYou } from "../steps/about-you";
import { StepEmployerProfile } from "../steps/employer-profile";
import { StepOrganization } from "../steps/organization";
import { StepProject } from "../steps/project";
import { StepCustomize } from "../steps/customize";
import { StepChoosePlan } from "../steps/choose-plan";
import { contentVariants } from "./constants";
import { WizardHeader } from "./header";
import { WizardFooter } from "./footer";
import { WizardRightPanel } from "./right-panel";

export type { OnboardingFormData } from "../../hooks/use-onboarding-wizard";

export function OnboardingWizard() {
  const {
    form,
    ready,
    currentStep,
    totalSteps,
    dir,
    creatingWorkspace,
    step2SubStep,
    enrichingEmployer,
    canContinue,
    submitting,
    next,
    back,
  } = useOnboardingWizard();

  const previewOrganizationName =
    useWatch({ control: form.control, name: "organization.name" }) ?? "";
  const previewOrganizationLogoUrl =
    useWatch({ control: form.control, name: "organization.logo" }) ?? "";
  const previewProjectTitle =
    useWatch({ control: form.control, name: "project.title" }) ?? "";
  const previewProjectCoverUrl =
    useWatch({ control: form.control, name: "project.cover_preview_url" }) ??
    "";

  if (!ready) {
    return <OnboardingPageSkeleton />;
  }

  const onEmployerSubStep = currentStep === 2 && step2SubStep === "employer";
  const enriching = onEmployerSubStep && enrichingEmployer;
  const formKey = onEmployerSubStep
    ? `form-${currentStep}-employer`
    : `form-${currentStep}`;

  const isPlanStep = currentStep === 5;

  return (
    <Form {...form}>
      <motion.div
        className={`flex w-full max-w-full flex-col overflow-hidden md:rounded-xl md:border border-contrast/8 lg:w-259.5 lg:max-w-full lg:flex-none lg:flex-row ${
          isPlanStep ? "lg:h-168" : "lg:h-163.5"
        }`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 250 }}
      >
        <div
          className={`flex min-h-0 md:bg-surface-elevated ${
            isPlanStep
              ? "w-full flex-1"
              : "w-full flex-1 items-start justify-center lg:w-[519px] lg:flex-none lg:shrink-0 lg:items-center lg:overflow-hidden"
          }`}
        >
          <div
            className={`flex flex-col justify-between w-full lg:min-h-0 lg:h-full ${
              onEmployerSubStep ? "h-full" : "min-h-full"
            } ${
              isPlanStep
                ? "px-6 py-6 lg:px-8"
                : enriching
                  ? "px-6 py-8"
                  : "max-w-md px-6 lg:px-0 py-8"
            }`}
          >
            <WizardHeader
              currentStep={currentStep}
              dir={dir}
              creatingWorkspace={creatingWorkspace}
              onEmployerSubStep={onEmployerSubStep}
            />

            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={formKey}
                custom={dir}
                variants={contentVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  type: "spring",
                  damping: 30,
                  stiffness: 350,
                  mass: 0.8,
                  delay: 0.03,
                }}
                className={`flex min-h-0 flex-1 flex-col ${
                  isPlanStep ? "gap-3" : "gap-4"
                }`}
              >
                {/* Outside ScrollArea so flex-1 can fill the left column height. */}
                {enriching ? (
                  <div className="flex min-h-0 flex-1 flex-col pt-4">
                    <EmployerSubStep enriching />
                  </div>
                ) : (
                  <ScrollArea
                    className={`min-h-0 flex-1 ${isPlanStep ? "pt-1 sm:pt-3" : "pt-4"}`}
                    constrainContentWidth
                    contentClassName={
                      isPlanStep
                        ? "flex flex-col gap-3"
                        : "flex flex-col gap-4"
                    }
                  >
                    {currentStep === 1 && <StepAboutYou />}
                    {currentStep === 2 && step2SubStep === "org" && (
                      <StepOrganization />
                    )}
                    {currentStep === 2 && step2SubStep === "employer" && (
                      <EmployerSubStep enriching={false} />
                    )}
                    {currentStep === 3 && <StepProject />}
                    {currentStep === 4 && <StepCustomize />}
                    {currentStep === 5 && (
                      <StepChoosePlan
                        onComplete={next}
                        submitting={submitting}
                      />
                    )}
                  </ScrollArea>
                )}

                <div className="shrink-0 pt-4">
                  <WizardFooter
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    onBack={back}
                    onNext={next}
                    canContinue={canContinue}
                    submitting={submitting}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {!isPlanStep && (
          <WizardRightPanel
            currentStep={currentStep}
            preview={{
              organizationLogoUrl: previewOrganizationLogoUrl,
              organizationName: previewOrganizationName,
              projectCoverUrl: previewProjectCoverUrl,
              projectTitle: previewProjectTitle,
            }}
          />
        )}
      </motion.div>
    </Form>
  );
}

function EmployerSubStep({ enriching }: { enriching: boolean }) {
  const orgName = useWatch({ name: "organization.name" }) as string | undefined;
  const orgLogo = useWatch({ name: "organization.logo" }) as string | undefined;
  return (
    <StepEmployerProfile
      orgName={orgName ?? ""}
      orgLogo={orgLogo ?? null}
      enriching={enriching}
    />
  );
}
